"use client";
import React, { useState, useRef } from "react";
import { X, Trash2, FileText, AlertCircle } from "lucide-react";
import { LuCloudUpload } from "react-icons/lu";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../../api/config";
import { buildHeaders } from "../../../api/buildHeaders";
import { PrimaryButton } from "../../../components/buttons/primaryButton/PrimaryButton.tsx";

export default function UploadCSVModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const modalRef = useRef(null);
  const [excelData, setExcelData] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      toast.error(t("home.toastErrorValidFile"));
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);

    if (selectedFile.type !== "text/csv") {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target?.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        setExcelData(jsonData);
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setExcelData([]);
    setUploadResult(null);
  };

  const truncateFileName = (name, maxLength = 22) => {
    if (!name) return "";
    const ext = name.includes(".xlsx") ? ".xlsx" : ".csv";
    if (name.length <= maxLength) return name;
    const visible = name.replace(ext, "");
    return visible.slice(0, maxLength - ext.length - 3) + "..." + ext;
  };

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setExcelData([]);
    setUploadResult(null);
    onClose();
  };

  const validateInstagramUsers = async (data) => {
    const invalidUsers = data.filter(
      (row) =>
        row["Instagram Username (can be empty)"] &&
        row["Instagram Username (can be empty)"].trim() === "",
    );
    return invalidUsers.length === 0;
  };

  const handleUploadFile = async () => {
    if (!file) {
      toast.error(t("home.toastErrorSelectFirst"));
      return;
    }

    if (excelData.length > 0) {
      const valid = await validateInstagramUsers(excelData);
      if (!valid) {
        toast.error(t("home.toastErrorInvalidInstagram"));
        return;
      }
    }

    setUploading(true);
    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error(t("home.toastErrorNoToken"));
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file, file.name);

      const headers = buildHeaders({ "Content-Type": undefined });

      const response = await fetch(
        `${REST_API_URL}/client/upload-clients-excel/`,
        {
          method: "POST",
          headers,
          body: formData,
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.detail || data?.message || "Upload failed");
      }

      // Check if backend returned partial or full errors list
      if (data && Array.isArray(data.errors) && data.errors.length > 0) {
        setUploadResult(data);
        if (data.created > 0 && onSuccess) {
          onSuccess();
        }
        return;
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", "new_client_added");
      }

      toast.success(t("home.toastSuccessUploaded"));
      setFile(null);
      setExcelData([]);
      setUploadResult(null);
      onClose();

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(t("home.toastErrorUpload") + (err.message || ""));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        "Full Name": "",
        "Instagram Username (can be empty)": "",
        "Email (must be a valid email or empty)": "",
        "Phone Number (ex: +12125551234 or empty)": "",
        "Birthday (can be empty, ex: DD/MM/YYYY)": "",
        "Country (can be empty)": "",
        "Family Status (can be empty)": "",
        "Hobbies (can be empty)": "",
        "About Service Notes (can be empty)": "",
        "Other Notes (can be empty)": "",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "clients_template.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success(t("home.toastSuccessTemplateDownloaded"));
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm z-50 p-4"
        onClick={handleClickOutside}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={`relative flex flex-col items-center bg-white border border-[#9A99AB33] rounded-2xl shadow-xl w-full ${
            uploadResult ? "max-w-[440px] p-6" : "max-w-[382px] px-6 py-6 pb-8"
          }`}
        >
          <PrimaryButton
            label=""
            leftIcon={<X size={22} strokeWidth={2} />}
            onClick={handleClose}
            variant="ghost"
            size="small"
            className="absolute top-2 right-2 !p-2 text-[#302B2B] hover:text-gray-500"
          />

          <AnimatePresence mode="wait">
            {uploadResult ? (
              <motion.div
                key="errors-view"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col items-center gap-4 w-full mt-2"
              >
                <div className="w-14 h-14 bg-rose-50 text-rose-500 border border-rose-100 rounded-full flex items-center justify-center">
                  <AlertCircle size={28} strokeWidth={2.2} />
                </div>

                <div className="text-center">
                  <h3 className="text-[19px] font-bold text-[#302B2B]">
                    {t("home.uploadErrorsTitle", "File Processing Errors")}
                  </h3>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {t("home.createdCount", {
                        count: uploadResult.created || 0,
                      })}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                      {t("home.errorsCount", {
                        count: uploadResult.errors?.length || 0,
                      })}
                    </span>
                  </div>
                </div>

                <div className="w-full text-left">
                  <p className="text-xs text-[#9A99AB] mb-2 font-medium">
                    {t("home.errorsFound", "Please review the errors below:")}
                  </p>

                  <div className="max-h-[220px] overflow-y-auto pr-1 space-y-2.5 scrollbar-thin scrollbar-thumb-gray-200">
                    {uploadResult.errors.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-rose-50/60 border border-rose-100/90 rounded-xl p-3 text-left transition-all"
                      >
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-rose-100 text-rose-800 tracking-wide uppercase">
                            {t("home.line", "Line")} {item.line}
                          </span>
                        </div>
                        <ul className="space-y-1 pl-1">
                          {item.errors?.map((err, errIdx) => (
                            <li
                              key={errIdx}
                              className="text-xs text-rose-700 flex items-start gap-1.5 leading-relaxed font-normal"
                            >
                              <span className="text-rose-500 font-bold mt-[-1px]">
                                •
                              </span>
                              <span>{err}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2.5 w-full mt-2">
                  <PrimaryButton
                    label={t("home.selectAnotherFile", "Choose another file")}
                    onClick={() => {
                      setUploadResult(null);
                      setFile(null);
                      setExcelData([]);
                    }}
                    variant="ghost"
                    className="flex-1 !py-2.5 border border-[#020637] text-[#020637] hover:bg-[#f5f6fb] justify-center text-xs sm:text-sm font-semibold rounded-xl"
                  />
                  <PrimaryButton
                    label={t("ok", "OK")}
                    onClick={handleClose}
                    className="flex-1 !py-2.5 justify-center text-xs sm:text-sm font-semibold rounded-xl"
                  />
                </div>
              </motion.div>
            ) : (
              <div className="flex flex-col items-center justify-center border-2 border-dashed border-[#020637] rounded-lg w-[322px] mt-6 p-8 gap-6">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="w-[92px] h-[92px] bg-[rgba(2,6,55,0.10)] rounded-full flex items-center justify-center"
                >
                  <LuCloudUpload
                    className="w-[60px] h-[60px] text-[#020637]"
                    strokeWidth={2.5}
                  />
                </motion.div>

                <AnimatePresence mode="wait">
                  {!file ? (
                    <motion.div
                      key="upload"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-3"
                    >
                      <h3 className="text-[20px] font-bold text-[#302B2B] text-center">
                        {t("home.modalUploadTitle")}
                      </h3>
                      <label className="border border-[#020637] text-[#020637] px-5 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#f5f6fb] transition">
                        {t("home.buttonSelectFile")}
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="text-xs text-[#9A99AB] mt-1">
                        {t("home.maxSizeLabel")}
                      </p>
                      <PrimaryButton
                        label={t("home.buttonDownloadTemplate")}
                        onClick={handleDownloadTemplate}
                        className="w-[290px] !py-3 justify-center"
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col items-center gap-4"
                    >
                      <h3 className="text-[20px] font-bold text-[#302B2B] text-center">
                        {t("home.modalSelectedTitle")}
                      </h3>

                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center justify-between border border-[#302B2B] rounded-xl px-4 py-3 w-[290px]"
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <FileText
                            size={20}
                            className="text-[#302B2B] flex-shrink-0"
                          />
                          <span
                            className="text-[14px] text-[#302B2B] truncate max-w-[190px]"
                            title={file.name}
                          >
                            {truncateFileName(file.name)}
                          </span>
                        </div>
                        <PrimaryButton
                          label=""
                          leftIcon={<Trash2 size={18} />}
                          onClick={handleRemoveFile}
                          variant="ghost"
                          size="small"
                          className="!p-2 text-[#302B2B] hover:text-red-500 hover:bg-red-50"
                        />
                      </motion.div>

                      <PrimaryButton
                        label={
                          uploading
                            ? t("home.stateUploading")
                            : t("home.buttonUploadFile")
                        }
                        onClick={handleUploadFile}
                        disabled={uploading}
                        className={`w-[290px] !py-3 justify-center ${
                          uploading ? "" : "!bg-green-500 hover:!bg-green-600"
                        }`}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

